import { gql, useQuery } from '@apollo/react-hooks';
import { type ApolloError } from '@apollo/react-hooks';
import dayjs from 'dayjs';
import type { HodDoSAssessment } from '../../HoDDoSAssessment';
import type {
  AcademicQualificationFormData,
  LanguageQualification,
  Mark,
  OtherTest,
} from '../../../../Application/graphql';
import { checkInvalidDate } from '../../../../Application/graphql';
import { HOD_DOS_DECISION } from '../../../HoDDoSAssessment';
import {
  conditionTypes,
  tuitionAssistantshipOptions,
  type EntranceRequirement,
  OFFER_TYPE,
  entranceRequirementOptions,
  TuitionAssistantship,
} from '../../../DRCAssessment';
import { getOptionsLabel } from '../../../../Monitor/components';
import { type AcademicRefereeData } from '../../../../Monitor/graphql';
import {
  awardClassificationOptions,
  fieldOfStudyOptions,
  levelOfDegreeOptions,
  mediumOfInstructionOptions,
  mediumOfQualificationOptions,
  modeOfStudyOptions,
} from '../../../../Application/components';
import { type ApplicationRemarkType } from '../../../../Nomination/graphql';

type DisplayHodDoSAssessment = Omit<HodDoSAssessment, 'remarks' | 'submitted'>;

export type StudentAssessmentDetail = {
  offerType: string;
  stipendApproval: boolean;
  conditionFulfillingDeadline: string;
  contactDepartmentPersonName: string;
  contactDepartmentPersonEmail: string;
  tuitionAssistantshipType: string;
  tuitionFeeAccountCode: string;
  isFirmOffer: boolean;
  conditions: string[];
  firstDegreeUL: string;
  firstDegreeAL: string;
  RL1Count: number;
  RL2Count: number;
  dorpgsRemarks: string;
  hodDosAssessment: DisplayHodDoSAssessment | null;
};

type GetStudentAcademic = {
  studentAcademic: AcademicQualificationFormData[];
  studentLanguage: any;
  studentOtherTest?: OtherTest;
  studentAcademicReferees: AcademicRefereeData[];
  studentAssessmentDetail: StudentAssessmentDetail;
  returnedRemarks: ApplicationRemarkType[];
  loading: boolean;
  error: ApolloError | undefined;
};

const NA = 'N/A';

const GET_STUDENT_ACADEMIC = gql`
  query ($id: ID!, $applicationId: String!) {
    programmeChoiceMetadata {
      countryList {
        value
        label
      }
    }

    application(id: $id) {
      remarks {
        fromRole
        toRole
        remark
        createdAt
      }
      academicReferees {
        title
        name {
          surname
          givenName
        }
        institution
        position
        relationship
        email
        referenceReport {
          name
          id
          link
        }
      }

      languageQualifications {
        needEnglishTest
        testType
        testDate
        referenceNo
        expectedResultUpdateDate
        resultAnnouncedIndicator
        result {
          resultReceiptDate
          listening
          reading
          writing
          speaking
          overall
        }
        supportingDocuments {
          id
          link
          name
        }
      }

      otherTests {
        testType
        testDate
        resultAnnouncedIndicator
        expectedResultUpdateDate
        referenceNo
        result {
          resultReceiptDate
          totalScore
          totalPercentile
          quantitativeScore
          quantitativePercentile
          analyticalWritingScore
          analyticalWritingPercentile
          verbalScore
          verbalPercentile
          integratedReasoningScore
          integratedReasoningPercentile
          dataInsightsScore
          dataInsightsPercentile
        }
        supportingDocuments {
          id
          link
          name
        }
      }

      academicQualifications {
        presentIndicator
        gpaIndicator
        rankIndicator
        academicCredentialIndicator
        degreeCertIndicator
        from
        to
        studyMode
        institution {
          country
          name
        }
        instructionMedium
        otherInstructionMedium
        currentStudyYear
        studyField
        degreeLevel
        qualificationMeans
        programmeName
        qualificationStatus
        academicOnlineVerificationCode
        degreeOnlineVerificationCode
        expectUpdateDate
        awardDate
        awardClassification
        withdrawDate
        officialClassRanking
        outOfRank
        officialMark
        outOfGpa
        convertGPARuleId
        marks {
          course
          credit
          result
        }
        markConversion {
          convertedMark
          letterToGPARule {
            name
          }
          digitToLetterRule {
            name
          }
        }
        certificates {
          id
          link
          name
        }
        transcripts {
          id
          link
          name
        }
        markExplanations {
          id
          link
          name
        }
        classRankProofs {
          id
          link
          name
        }
        otherDocuments {
          id
          link
          name
        }
      }
    }
    assessmentDetail(applicationId: $applicationId) {
      hodDosAssessment {
        externalGrantOrDEA {
          required
          confirmation
          accountCode
        }
        nonSTEMOrNonSHS {
          required
          confirmation
          accountCode
          assumptionDate
        }
        competitiveFund {
          required
          confirmation
          accountCode
        }
        dualPhD {
          required
          confirmation
          accountCode
        }
        decision
      }
      dsrcAdmissionForm {
        isFirmOffer
        conditions
        stipendApproval
        tuitionAssistantshipType
        tuitionFeeAccountCode
        conditionFulfillingDeadline
        contactDepartmentPersonName
        contactDepartmentPersonEmail
      }
      dsrcAssessment {
        dsrcDeclaration {
          remarks
        }
        dsrcGrading {
          firstDegreeUL
          firstDegreeAL
          RL1Count
          RL2Count
        }
      }
    }
  }
`;

export const useGetStudentAcademic = (id: string): GetStudentAcademic => {
  const { data, loading, error } = useQuery(GET_STUDENT_ACADEMIC, {
    variables: { id, applicationId: id },
    skip: !id,
  });

  const {
    academicQualifications,
    languageQualifications,
    otherTests,
    academicReferees,
    remarks: returnRemarks,
  } = data?.application ?? {};
  const countryList = data?.programmeChoiceMetadata?.countryList ?? [];
  const academicQualificationsData: AcademicQualificationFormData[] = (
    academicQualifications ?? []
  ).map((academicQualification: any) => {
    const {
      presentIndicator,
      from,
      to,
      currentStudyYear = NA,
      studyMode = NA,
      institution,
      instructionMedium = NA,
      otherInstructionMedium = NA,
      studyField = NA,
      degreeLevel = NA,
      qualificationMeans = NA,
      programmeName = NA,
      qualificationStatus = NA,
      awardDate = NA,
      awardClassification = NA,
      officialMark = NA,
      outOfGpa = NA,
      marks = [],
      convertGPARuleId = '',
      markConversion = NA,
      expectUpdateDate = NA,
      withdrawDate = NA,
      academicOnlineVerificationCode = NA,
      degreeOnlineVerificationCode = NA,
      officialClassRanking = NA,
      outOfRank = NA,
      certificates = NA,
      transcripts = NA,
      markExplanations = NA,
      classRankProofs,
      otherDocuments,
      gpaIndicator,
      rankIndicator,
      academicCredentialIndicator,
      degreeCertIndicator,
    } = academicQualification ?? {};

    return {
      from: checkInvalidDate(from) ? NA : dayjs(from).format('DD/MM/YYYY'),
      to: (presentIndicator as boolean)
        ? 'present'
        : dayjs(to).format('DD/MM/YYYY'),
      currentStudyYear,
      studyMode: getOptionsLabel(modeOfStudyOptions, studyMode),
      institutionName: institution?.name ?? NA,
      institutionCountry: getOptionsLabel(countryList, institution?.country),
      instructionMedium:
        instructionMedium === 'OTHERS'
          ? otherInstructionMedium
          : getOptionsLabel(mediumOfInstructionOptions, instructionMedium),
      studyField: getOptionsLabel(fieldOfStudyOptions, studyField),
      degreeLevel: getOptionsLabel(levelOfDegreeOptions, degreeLevel),
      qualificationMeans: getOptionsLabel(
        mediumOfQualificationOptions,
        qualificationMeans,
      ),
      programmeName,
      qualificationStatus,
      awardDate: checkInvalidDate(awardDate)
        ? NA
        : dayjs(awardDate).format('DD/MM/YYYY'),
      awardClassification: getOptionsLabel(
        awardClassificationOptions,
        awardClassification,
      ),
      officialMark,
      outOfGpa,
      convertGPARuleId,
      marks: marks as Mark[],
      convertedMark: markConversion?.convertedMark ?? NA,
      letterToGPARule: markConversion?.letterToGPARule?.name ?? NA,
      digitToLetterRule: markConversion?.digitToLetterRule?.name ?? NA,
      expectUpdateDate: checkInvalidDate(expectUpdateDate)
        ? NA
        : dayjs(expectUpdateDate).format('DD/MM/YYYY'),
      withdrawDate: checkInvalidDate(withdrawDate)
        ? NA
        : dayjs(withdrawDate).format('DD/MM/YYYY'),
      academicOnlineVerificationCode: (academicCredentialIndicator as boolean)
        ? academicOnlineVerificationCode
        : NA,
      degreeOnlineVerificationCode: (degreeCertIndicator as boolean)
        ? degreeOnlineVerificationCode
        : NA,
      officialClassRanking,
      outOfRank,
      certificates,
      transcripts,
      markExplanations,
      classRankProofs,
      otherDocuments,
      gpaIndicator,
      rankIndicator,
    };
  });
  const studentAcademic = academicQualificationsData;

  const languageQualificationData = languageQualifications?.[0] ?? {};

  const studentLanguage: LanguageQualification = {
    needEnglishTest: languageQualificationData?.needEnglishTest ?? false,
    testType: languageQualificationData?.testType ?? '',
    testDate: checkInvalidDate(languageQualificationData?.testDate)
      ? NA
      : dayjs(languageQualificationData?.testDate).format('DD/MM/YYYY'),
    resultAnnouncedIndicator:
      languageQualificationData?.resultAnnouncedIndicator ?? false,
    expectedResultUpdateDate: checkInvalidDate(
      languageQualificationData?.expectedResultUpdateDate,
    )
      ? NA
      : dayjs(languageQualificationData?.expectedResultUpdateDate).format(
          'DD/MM/YYYY',
        ),
    result: {
      resultReceiptDate: checkInvalidDate(
        languageQualificationData?.result?.resultReceiptDate,
      )
        ? NA
        : dayjs(languageQualificationData?.result?.resultReceiptDate).format(
            'DD/MM/YYYY',
          ),
      listening: languageQualificationData?.result?.listening,
      writing: languageQualificationData?.result?.writing,
      speaking: languageQualificationData?.result?.speaking,
      reading: languageQualificationData?.result?.reading,
      overall: languageQualificationData?.result?.overall,
    },
    referenceNo: languageQualificationData?.referenceNo,
    supportingDocuments: languageQualificationData?.supportingDocuments,
  };
  const studentOtherTest = otherTests?.[0];
  const dsrcAdmissionForm = data?.assessmentDetail?.dsrcAdmissionForm ?? {};

  const stipendApproval: boolean = dsrcAdmissionForm?.stipendApproval ?? false;

  const tuitionAssistantshipType: TuitionAssistantship =
    dsrcAdmissionForm?.tuitionAssistantshipType ?? TuitionAssistantship.NA;

  const tuitionFeeAccountCode =
    dsrcAdmissionForm?.tuitionAssistantshipType === TuitionAssistantship.NA
      ? NA
      : (dsrcAdmissionForm?.tuitionFeeAccountCode ?? NA);

  const isFirmOffer: boolean = dsrcAdmissionForm?.isFirmOffer ?? false;
  const entranceRequirement = isFirmOffer
    ? OFFER_TYPE.FIRM
    : OFFER_TYPE.CONDITION;
  const dsrcGrading = data?.assessmentDetail?.dsrcAssessment?.dsrcGrading ?? {};
  const dsrcDeclaration =
    data?.assessmentDetail?.dsrcAssessment?.dsrcDeclaration ?? {};

  const offerType = getOptionsLabel(
    entranceRequirementOptions,
    entranceRequirement,
  );
  const conditions: EntranceRequirement[] = dsrcAdmissionForm?.conditions ?? [];
  const conditionsLabel: string[] = conditions.map(con =>
    getOptionsLabel(conditionTypes, con),
  );

  let hodDosAssessment: DisplayHodDoSAssessment | null = {
    externalGrantOrDEA: {
      required: false,
      confirmation: false,
      accountCode: '',
    },
    nonSTEMOrNonSHS: {
      required: false,
      confirmation: false,
      assumptionDate: '',
      accountCode: '',
    },
    competitiveFund: {
      required: false,
      confirmation: false,
      accountCode: '',
    },
    dualPhD: {
      required: false,
      confirmation: false,
      accountCode: '',
    },
    decision: HOD_DOS_DECISION.NA,
  };

  if (!loading) {
    if (
      data?.assessmentDetail?.hodDosAssessment !== null &&
      data?.assessmentDetail?.hodDosAssessment !== undefined
    ) {
      const {
        externalGrantOrDEA,
        nonSTEMOrNonSHS,
        competitiveFund,
        dualPhD,
        decision,
      } = data?.assessmentDetail?.hodDosAssessment ?? {};
      hodDosAssessment = {
        externalGrantOrDEA: {
          required: externalGrantOrDEA?.required ?? false,
          confirmation: externalGrantOrDEA?.confirmation ?? false,
          accountCode: externalGrantOrDEA?.accountCode ?? NA,
        },
        nonSTEMOrNonSHS: {
          required: nonSTEMOrNonSHS?.required ?? false,
          confirmation: nonSTEMOrNonSHS?.confirmation ?? false,
          assumptionDate: checkInvalidDate(nonSTEMOrNonSHS?.assumptionDate)
            ? NA
            : dayjs(nonSTEMOrNonSHS?.assumptionDate).format('DD/MM/YYYY'),
          accountCode: nonSTEMOrNonSHS?.accountCode ?? NA,
        },
        competitiveFund: {
          required: competitiveFund?.required ?? false,
          confirmation: competitiveFund?.confirmation ?? false,
          accountCode: competitiveFund?.accountCode ?? NA,
        },
        dualPhD: {
          required: dualPhD?.required ?? false,
          confirmation: dualPhD?.confirmation ?? false,
          accountCode: dualPhD?.accountCode ?? NA,
        },

        decision:
          decision === null
            ? HOD_DOS_DECISION.NA
            : (decision as HOD_DOS_DECISION),
      };
    } else hodDosAssessment = null;
  }

  const conditionFulfillingDeadline = checkInvalidDate(
    dsrcAdmissionForm?.conditionFulfillingDeadline,
  )
    ? NA
    : dayjs(dsrcAdmissionForm?.conditionFulfillingDeadline).format(
        'DD/MM/YYYY',
      );

  const studentAssessmentDetail: StudentAssessmentDetail = {
    offerType,
    isFirmOffer,
    conditions: conditionsLabel,
    conditionFulfillingDeadline,
    contactDepartmentPersonName:
      dsrcAdmissionForm?.contactDepartmentPersonName ?? NA,
    contactDepartmentPersonEmail:
      dsrcAdmissionForm?.contactDepartmentPersonEmail ?? NA,
    stipendApproval,
    firstDegreeUL: dsrcGrading?.firstDegreeUL ?? '-',
    firstDegreeAL: dsrcGrading?.firstDegreeAL ?? '-',
    RL1Count: dsrcGrading?.RL1Count ?? 0,
    RL2Count: dsrcGrading?.RL2Count ?? 0,
    tuitionAssistantshipType: getOptionsLabel(
      tuitionAssistantshipOptions,
      tuitionAssistantshipType,
    ),
    dorpgsRemarks: dsrcDeclaration?.remarks ?? '',
    tuitionFeeAccountCode,
    hodDosAssessment,
  };

  const studentAcademicReferees: AcademicRefereeData[] = (
    academicReferees ?? []
  ).map((academicReferee: any) => {
    const {
      title = NA,
      name,
      institution = NA,
      position = NA,
      relationship = NA,
      email = NA,
      referenceReport,
    } = academicReferee ?? {};

    return {
      title,
      name,
      institution,
      position,
      relationship,
      email,
      referenceReport: {
        id: referenceReport?.id ?? '',
        name: referenceReport?.name ?? '',
        link: referenceReport?.link ?? '',
      },
    };
  });

  const returnedRemarks: ApplicationRemarkType[] = returnRemarks ?? [];

  return {
    studentAcademic,
    studentLanguage,
    studentOtherTest,
    studentAcademicReferees,
    studentAssessmentDetail,
    returnedRemarks,
    loading,
    error,
  };
};
